import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, Link } from "wouter";

import Button from "react-bootstrap/Button";
import Spinner from "react-bootstrap/Spinner";
import Alert from "react-bootstrap/Alert";

import { absURL } from '../utils';
import QuizApi from '../QuizApi';
import FileUploadMultiple from './FileUploadMultiple';
import AdminHeader from './AdminHeader';
import InfoTooltip from './InfoTooltip';
import DeezerSearch from './DeezerSearch';

export default function AdminQuizQuestionCreate(props) {

  const { quizId } = props;

  const [, setLocation] = useLocation();
  const [questionText, setQuestionText] = useState("");
  const [questionType, setQuestionType] = useState(1);
  const [questionMaxPoints, setQuestionMaxPoints] = useState(1);
  const [questionSingleAnswer, setQuestionSingleAnswer] = useState(false);
  const [feedbackMarkdown, setFeedbackMarkdown] = useState("");
  const [inputMode, setInputMode] = useState(false);
  const [acceptedAnswers, setAcceptedAnswers] = useState([""]);
  const [quizDetails, setQuizDetails] = useState(null);

  // Vald Deezer-låt (endast för Musicquiz)
  const [selectedTrack, setSelectedTrack] = useState(null);

  const [answerList, setAnswerList] = useState([
    { text: "", correct: false },
    { text: "", correct: false }
  ]);

  const [fileList, setFileList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [displayError, setDisplayError] = useState(false);
  const [submitErrorMsg, setSubmitErrorMsg] = useState(false);

  const isMusicQuiz = quizDetails && quizDetails.quiz.type === "Musicquiz";

  useEffect(() => {

    const fetch = async () => {

      setLoading(true);

      const result = await QuizApi.quizDetails(quizId);

      if (result && result.response) {
        setQuizDetails(result.response);
      }

      setLoading(false);
    }

    fetch();

  }, [quizId]);

  function setAnswerText(index, text) {
    setAnswerList([
      ...answerList.slice(0, index),
      { ...answerList[index], text },
      ...answerList.slice(index + 1)]
    );
  }

  function setAnswerCorrect(index, correct) {
    setAnswerList([
      ...answerList.slice(0, index),
      { ...answerList[index], correct },
      ...answerList.slice(index + 1)]
    );
  }

  // =======================
  // Form submit callback
  // =======================
  const onSubmit = useCallback(ev => {

    ev.preventDefault();

    if (!(questionText || "").trim()) {
      setDisplayError(true);
      return;
    }

    // Musicquiz kräver en vald låt
    if (isMusicQuiz && !selectedTrack) {
      setDisplayError(true);
      setSubmitErrorMsg("Du måste välja en låt från Deezer.");
      return;
    }

    setLoading(true);

    const trimmedAcceptedAnswers = acceptedAnswers
      .map(a => (a || "").trim())
      .filter(a => a.length > 0);

    const data = new FormData();
    data.append("questionText", questionText);
    data.append("questionType", questionType);
    data.append("questionMaxPoints", questionMaxPoints);
    data.append("questionSingleAnswer", questionSingleAnswer);
    data.append("feedbackMarkdown", feedbackMarkdown);
    data.append("inputMode", inputMode);
    data.append("acceptedAnswersJSON", JSON.stringify(trimmedAcceptedAnswers));
    data.append("answerListJSON", JSON.stringify(inputMode ? [] : answerList));

    // Skicka med Deezer-låtdata om det är ett musikquiz
    if (isMusicQuiz && selectedTrack) {
      data.append("externalMediaJSON", JSON.stringify([{
        source: "deezer",
        trackId:    selectedTrack.id,
        title:      selectedTrack.title,
        artist:     selectedTrack.artist,
        album:      selectedTrack.album,
        cover:      selectedTrack.cover,
        previewUrl: selectedTrack.previewUrl,
        deezerUrl:  selectedTrack.deezerUrl,
        mimeType:   "audio/mpeg"
      }]));
    }

    if (fileList) {
      for (const file of fileList) {
        data.append("questionMedia", file, file.name);
      }
    }

    const fn = async () => {

      let result = null;

      try {
        let httpResponse = await fetch(`/api/quiz/${quizId}/questionCreate`, {
          method: 'POST',
          body: data
        });

        result = await httpResponse.json();
      } catch (e) {
        setDisplayError(true);
        setSubmitErrorMsg(e.message);
      }

      setLoading(false);

      if (result && result.success) {
        setDisplayError(false);
        setSubmitErrorMsg("");
        setQuestionText("");
        setQuestionType(1);
        setQuestionMaxPoints(1);
        setFileList([]);
        setSelectedTrack(null);
        setLocation(absURL(`/admin/quiz/${quizId}/questions`));
      }
      else {
        setDisplayError(true);
        setSubmitErrorMsg("Det uppstod ett fel när vi försökte skapa frågan.");
      }
    };

    fn();

  }, [
    quizId, questionText, questionType,
    questionMaxPoints, questionSingleAnswer,
    feedbackMarkdown, inputMode, acceptedAnswers,
    answerList, fileList, selectedTrack, isMusicQuiz, setLocation
  ]);

  return (
    <div id="quizQuestionCreate">
      <div>

        {loading && (
          <div className="d-flex justify-content-center align-items-center h-100">
            <Spinner animation="border" />
          </div>
        )}

        {!loading &&
          <div>
            <AdminHeader />
            <div className="adminContentWrapper">

              <Button variant="outline-secondary" size="sm" onClick={() => window.history.back()}>
                Tillbaka
              </Button>

              <form onSubmit={onSubmit}>

                <div className="form-group mt-2">
                  <label htmlFor="questionMarkdown">Frågetext:</label>
                  <textarea
                    className="form-control"
                    cols="40" rows="5"
                    id="questionMarkdown"
                    name="questionMarkdown"
                    value={questionText}
                    onChange={ev => setQuestionText(ev.target.value)}>
                  </textarea>
                </div>

                {displayError && !(questionText || "").trim() && <Alert variant={"danger"}>
                  Du måste ange en frågetext
                </Alert>
                }

                {/* ================================
                    Deezer-sökning för Musicquiz
                    ================================ */}
                {isMusicQuiz && (
                  <DeezerSearch
                    selectedTrack={selectedTrack}
                    onTrackSelected={track => {
                      setSelectedTrack(track);
                      setDisplayError(false);
                      setSubmitErrorMsg("");
                    }}
                  />
                )}

                {displayError && isMusicQuiz && !selectedTrack && (
                  <Alert variant="danger">Du måste välja en låt från Deezer.</Alert>
                )}

                <div className="form-group mt-2">
                  <label htmlFor="feedbackMarkdown">
                    Ev. Feedback{" "}
                    <span className="font-italic">
                      (visas för deltagare efter svar — framtida funktion):
                    </span>
                  </label>
                  <textarea
                    className="form-control"
                    cols="40" rows="3"
                    id="feedbackMarkdown"
                    name="feedbackMarkdown"
                    value={feedbackMarkdown}
                    onChange={ev => setFeedbackMarkdown(ev.target.value)}>
                  </textarea>
                </div>

                {/* Filuppladdning visas bara för icke-musikquiz */}
                {!isMusicQuiz && (
                  <div className="form-group">
                    <label>
                      Filer <span className="font-italic">(endast bilder kommer att visas i quizläget):</span>
                    </label>
                    <FileUploadMultiple onFilesChanged={newFileList => setFileList(newFileList)} />
                  </div>
                )}

                {quizDetails && quizDetails.quiz.type === "Quiz" &&
                  <>
                    <div className="form-group">
                      <label htmlFor="questionType">Poängtyp:</label>
                      <InfoTooltip placement="left" className="ml-1">
                        <b>Allt eller inget</b>
                        <p>Om alla rätta alternativ väljs så ges maxpoäng, annars noll poäng.</p>
                        <b>Procentandel</b>
                        <p>Poäng tilldelas som en andel av maxpoäng för varje rätt svar.</p>
                      </InfoTooltip>
                      <select name="questionType" className="form-control" onChange={ev => setQuestionType(ev.target.value)}>
                        <option value="1">Allt eller inget</option>
                        <option value="2">Procentandel</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label htmlFor="questionMaxPoints">Maxpoäng:</label>
                      <input
                        type="number"
                        name="questionMaxPoints"
                        id="questionMaxPoints"
                        className="form-control"
                        value={questionMaxPoints} min="1"
                        onChange={ev => setQuestionMaxPoints(ev.target.value)} />
                    </div>
                  </>
                }

                {/* Musicquiz: poäng och maxpoäng precis som Quiz */}
                {isMusicQuiz &&
                  <>
                    <div className="form-group mt-2">
                      <label htmlFor="questionType">Poängtyp:</label>
                      <InfoTooltip placement="left" className="ml-1">
                        <b>Allt eller inget</b>
                        <p>Om alla rätta alternativ väljs så ges maxpoäng, annars noll poäng.</p>
                        <b>Procentandel</b>
                        <p>Poäng tilldelas som en andel av maxpoäng för varje rätt svar.</p>
                      </InfoTooltip>
                      <select name="questionType" className="form-control" onChange={ev => setQuestionType(ev.target.value)}>
                        <option value="1">Allt eller inget</option>
                        <option value="2">Procentandel</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label htmlFor="questionMaxPoints">Maxpoäng:</label>
                      <input
                        type="number"
                        name="questionMaxPoints"
                        id="questionMaxPoints"
                        className="form-control"
                        value={questionMaxPoints} min="1"
                        onChange={ev => setQuestionMaxPoints(ev.target.value)} />
                    </div>
                  </>
                }

                {(quizDetails && quizDetails.quiz.type === "Quiz" || isMusicQuiz) &&
                  <div className="form-check mt-2">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="inputMode"
                      checked={inputMode}
                      onChange={ev => setInputMode(ev.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="inputMode">
                      Inputläge (svar via textfält)
                      <InfoTooltip placement="left" className="ml-1">
                        <p>Deltagaren skriver sitt svar i ett textfält istället för att välja bland alternativ. Svaret jämförs (utan skiftläge och kringliggande mellanslag) mot de accepterade svaren.</p>
                      </InfoTooltip>
                    </label>
                  </div>
                }

                {(quizDetails && quizDetails.quiz.type === "Quiz" || isMusicQuiz) && inputMode &&
                  <div className="form-group mt-3">
                    <label>Accepterade svar:</label>
                    <div>
                      {acceptedAnswers.map((value, idx) =>
                        <div className="form-inline" key={idx}>
                          <input
                            type="text"
                            value={value}
                            onChange={ev => setAcceptedAnswers([
                              ...acceptedAnswers.slice(0, idx),
                              ev.target.value,
                              ...acceptedAnswers.slice(idx + 1)
                            ])}
                            className="form-control mt-2 mr-2"
                            placeholder="Ett accepterat svar (t.ex. 'ABBA' eller 'Dancing Queen')" />
                          {acceptedAnswers.length > 1 &&
                            <span
                              onClick={() => setAcceptedAnswers(acceptedAnswers.filter((_, i) => i !== idx))}
                              className="btn btn-sm btn-quizSecondaryDark ml-1">
                              Ta bort
                            </span>
                          }
                        </div>
                      )}
                    </div>
                    <Button
                      type="button"
                      className="btn-sm mt-2"
                      onClick={() => setAcceptedAnswers([...acceptedAnswers, ""])}>
                      Lägg till accepterat svar
                    </Button>
                  </div>
                }

                {quizDetails && (quizDetails.quiz.type === "Quiz" || isMusicQuiz) && !inputMode &&
                  <div>
                    <div>
                      <input
                        type="checkbox"
                        className="mr-1"
                        id="questionSingleAnswer"
                        checked={questionSingleAnswer}
                        onChange={ev => setQuestionSingleAnswer(ev.target.checked)}
                      />
                      <label className="form-check-label" htmlFor="questionSingleAnswer">
                        Deltagare får endast ange ett svar
                      </label>
                    </div>

                    <label htmlFor="answerList" className="mt-3">Svarsalternativ:</label>

                    <div>
                      {answerList.map((a, idx) =>
                        <div className="form-inline" key={idx}>
                          <div className="form-group mr-3">
                            <input
                              type="text"
                              onChange={ev => setAnswerText(idx, ev.target.value)}
                              value={answerList[idx].text}
                              className="form-control mt-2"
                              placeholder="Skriv en svarstext" />
                          </div>
                          <div className="form-group">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id={`questionAnswerCorrect_${idx}`}
                              checked={answerList[idx].correct}
                              onChange={ev => setAnswerCorrect(idx, ev.target.checked)}
                            />
                            <label className="form-check-label" htmlFor={`questionAnswerCorrect_${idx}`}>
                              Korrekt alternativ?
                            </label>
                          </div>
                        </div>
                      )}
                    </div>

                    <Button
                      type="button"
                      className="btn-sm mt-2"
                      onClick={() => setAnswerList([...answerList, { text: "", correct: false }])}>
                      Nytt svarsalternativ
                    </Button>
                  </div>
                }

                {quizDetails && quizDetails.quiz.type === "Poll" && !inputMode &&
                  <div>
                    <div>
                      <input
                        type="checkbox"
                        className="mr-1"
                        id="questionSingleAnswer"
                        checked={questionSingleAnswer}
                        onChange={ev => setQuestionSingleAnswer(ev.target.checked)}
                      />
                      <label className="form-check-label" htmlFor="questionSingleAnswer">
                        Deltagare får endast ange ett svar
                      </label>
                    </div>

                    <label className="mt-3">Svarsalternativ:</label>

                    <div>
                      {answerList.map((a, idx) =>
                        <div className="form-inline" key={idx}>
                          <div className="form-group mr-3">
                            <input
                              type="text"
                              onChange={ev => setAnswerText(idx, ev.target.value)}
                              value={answerList[idx].text}
                              className="form-control mt-2"
                              placeholder="Skriv en svarstext" />
                          </div>
                        </div>
                      )}
                    </div>

                    <Button
                      type="button"
                      className="btn-sm mt-2"
                      onClick={() => setAnswerList([...answerList, { text: "", correct: false }])}>
                      Nytt svarsalternativ
                    </Button>
                  </div>
                }

                <Button type="submit" className="btn mt-4 btn-quizPrimaryDark">Skapa fråga</Button>

                {displayError && submitErrorMsg &&
                  <Alert variant={"danger"} className="mt-4">
                    {submitErrorMsg}
                  </Alert>
                }

              </form>

            </div>
          </div>
        }
      </div>
    </div>
  );
};
